import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '../api';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import { Stethoscope, UserRound, CalendarCheck, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b'];

const StatusBadge = ({ status }) => {
    const map = {
        Accepted: 'badge-success',
        Completed: 'badge-success',
        Pending: 'badge-warning',
        Assigned: 'badge-info',
        Cancelled: 'badge-danger',
        Timeout: 'badge-danger',
    };
    return (
        <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>
    );
};

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        dashboardAPI
            .getStats()
            .then((res) => setData(res.data.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <div className="loading-container">
                <div className="spinner" />
            </div>
        );
    if (!data) return <div>Failed to load dashboard</div>;

    const { stats, recentAppointments, monthlyData, doctorStatus } = data;

    const statCards = [
        {
            label: 'Total Doctors',
            value: stats.totalDoctors,
            icon: Stethoscope,
            color: 'blue',
        },
        {
            label: 'Total Patients',
            value: stats.totalPatients,
            icon: UserRound,
            color: 'purple',
        },
        {
            label: 'Total Appointments',
            value: stats.totalAppointments,
            icon: CalendarCheck,
            color: 'green',
        },
        {
            label: 'Pending',
            value: stats.pendingAppointments,
            icon: Clock,
            color: 'orange',
        },
    ];

    return (
        <div className="fade-in">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Dashboard</h1>
                    <p className="page-subtitle">
                        Welcome back! Here's what's happening today.
                    </p>
                </div>
            </div>

            {/* Stats */}
            <div className="grid-4" style={{ marginBottom: 28 }}>
                {statCards.map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className={`stat-card ${color}`}>
                        <div className={`stat-icon ${color}`}>
                            <Icon size={20} />
                        </div>
                        <div className="stat-value">{value}</div>
                        <div className="stat-label">{label}</div>
                    </div>
                ))}
            </div>

            {/* Charts */}
            <div className="grid-2" style={{ marginBottom: 28 }}>
                {/* Monthly appointments chart */}
                <div className="card">
                    <h3
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 16,
                            fontWeight: 600,
                            marginBottom: 20,
                        }}
                    >
                        Monthly Appointments
                    </h3>
                    {monthlyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={monthlyData}>
                                <XAxis
                                    dataKey="month"
                                    stroke="#64748b"
                                    fontSize={12}
                                />
                                <YAxis stroke="#64748b" fontSize={12} />
                                <Tooltip
                                    contentStyle={{
                                        background: '#1e293b',
                                        border: '1px solid #1e2d45',
                                        borderRadius: 8,
                                        color: '#f1f5f9',
                                    }}
                                />
                                <Bar
                                    dataKey="count"
                                    fill="#3b82f6"
                                    radius={[4, 4, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="empty-state" style={{ padding: 40 }}>
                            No data yet
                        </div>
                    )}
                </div>

                {/* Doctor status pie */}
                <div className="card">
                    <h3
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 16,
                            fontWeight: 600,
                            marginBottom: 20,
                        }}
                    >
                        Doctor Status
                    </h3>
                    {doctorStatus.length > 0 ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 20,
                            }}
                        >
                            <ResponsiveContainer width="60%" height={220}>
                                <PieChart>
                                    <Pie
                                        data={doctorStatus}
                                        dataKey="count"
                                        nameKey="status"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        innerRadius={40}
                                    >
                                        {doctorStatus.map((entry, i) => (
                                            <Cell
                                                key={i}
                                                fill={COLORS[i % COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            background: '#1e293b',
                                            border: '1px solid #1e2d45',
                                            borderRadius: 8,
                                            color: '#f1f5f9',
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ flex: 1 }}>
                                {doctorStatus.map((entry, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            marginBottom: 10,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                background:
                                                    COLORS[i % COLORS.length],
                                                flexShrink: 0,
                                            }}
                                        />
                                        <span
                                            style={{
                                                fontSize: 13,
                                                color: 'var(--text2)',
                                            }}
                                        >
                                            {entry.status}
                                        </span>
                                        <span
                                            style={{
                                                marginLeft: 'auto',
                                                fontSize: 14,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {entry.count}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="empty-state" style={{ padding: 40 }}>
                            No data yet
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Appointments */}
            <div className="card">
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 20,
                    }}
                >
                    <h3
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 16,
                            fontWeight: 600,
                        }}
                    >
                        Recent Appointments
                    </h3>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/appointments')}
                    >
                        View All
                    </button>
                </div>
                <div
                    className="table-wrapper"
                    style={{ border: 'none', borderRadius: 0 }}
                >
                    <table>
                        <thead>
                            <tr>
                                <th>Patient</th>
                                <th>Doctor</th>
                                <th>Type</th>
                                <th>Date & Time</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentAppointments.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={5}
                                        style={{
                                            textAlign: 'center',
                                            color: 'var(--text3)',
                                            padding: 40,
                                        }}
                                    >
                                        No appointments yet
                                    </td>
                                </tr>
                            ) : (
                                recentAppointments.map((a) => (
                                    <tr
                                        key={a.appointment_id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            navigate(
                                                `/appointments/${a.appointment_id}`
                                            )
                                        }
                                    >
                                        <td>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                }}
                                            >
                                                <div
                                                    className="avatar"
                                                    style={{
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {(a.patient_name || 'P')[0]}
                                                </div>
                                                <span
                                                    style={{
                                                        color: 'var(--text)',
                                                    }}
                                                >
                                                    {a.patient_name || '—'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>{a.doctor_name || '—'}</td>
                                        <td>
                                            <span className="tag">
                                                {a.appointment_type}
                                            </span>
                                        </td>
                                        <td>
                                            {a.appointment_date}{' '}
                                            {a.appointment_time}
                                        </td>
                                        <td>
                                            <StatusBadge status={a.status} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
